import React from "react";
import { makeStyles, Paper, Typography, Divider, Box } from "@material-ui/core";
import {
    ShoppingCart as CartIcon,
    CheckCircle as SuccessIcon,
    Payment as PaymentIcon,
    Receipt as ReceiptIcon
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(1.5),
        minWidth: 250,
        maxWidth: 320,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        border: `1px solid ${theme.palette.divider}`,
    },
    header: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    headerText: {
        fontWeight: "bold",
        marginLeft: theme.spacing(1),
    },
    body: {
        marginBottom: theme.spacing(1.5),
    },
    itemRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: theme.spacing(0.5),
    },
    itemInfo: {
        display: "flex",
        alignItems: "center",
        fontSize: "0.85rem",
    },
    footer: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    totalRow: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: theme.spacing(1),
    },
    statusBadge: {
        alignSelf: "flex-start",
        padding: "2px 8px",
        borderRadius: 12,
        fontSize: "0.75rem",
        fontWeight: "bold",
        marginTop: theme.spacing(1),
    },
    statusCompleted: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
    },
    statusPending: {
        backgroundColor: "#fff3e0",
        color: "#ef6c00",
    }
}));

const InteractiveMessageView = ({ msg }) => {
    const classes = useStyles();

    const parseMessage = (body) => {
        try {
            const data = typeof body === "string" ? JSON.parse(body) : body;

            // Caso 1: InteractiveMessage direto ou dentro de corpo
            const interactive = data.interactiveMessage || data.InteractiveMessage || data;

            if (!interactive) return null;

            // Extrair Título, Corpo e Rodapé base do WhatsApp
            const title = interactive.header?.title || interactive.body?.text || "Pedido/Pagamento";
            const footerText = interactive.footer?.text || "";

            // Extrair Detalhes do Pedido do NativeFlowMessage
            const nativeFlow = interactive.nativeFlowMessage || interactive.NativeFlowMessage;
            let orderData = null;
            let buttons = [];

            if (nativeFlow && nativeFlow.buttons) {
                buttons = nativeFlow.buttons;
                const reviewOrderBtn = buttons.find(b => b.name === "revieworder" || b.name === "reviewandpay");
                if (reviewOrderBtn && reviewOrderBtn.buttonParamsJSON) {
                    try {
                        const params = JSON.parse(reviewOrderBtn.buttonParamsJSON);
                        orderData = params.order;
                    } catch (e) {
                        console.error("Erro ao parsear buttonParamsJSON", e);
                    }
                }
            }

            return { title, footerText, orderData, buttons };
        } catch (e) {
            console.error("Erro ao parsear InteractiveMessage JSON", e);
            return null;
        }
    };

    const parsed = parseMessage(msg.body);

    if (!parsed) return <Typography variant="body2">{msg.body}</Typography>;

    const { title, orderData } = parsed;

    return (
        <Paper className={classes.container} elevation={0}>
            <Box className={classes.header}>
                <CartIcon fontSize="small" />
                <Typography variant="subtitle2" className={classes.headerText}>
                    {title}
                </Typography>
            </Box>

            <Divider />

            <Box className={classes.body}>
                {orderData?.items?.map((item, idx) => (
                    <Box key={idx} className={classes.itemRow}>
                        <Box className={classes.itemInfo}>
                            <ReceiptIcon fontSize="inherit" style={{ marginRight: 4, opacity: 0.6 }} />
                            <Typography variant="body2">{item.name}</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                            {item.quantity}x
                        </Typography>
                    </Box>
                ))}

                {orderData?.subtotal && (
                    <Box className={classes.totalRow}>
                        <Typography variant="caption" color="textSecondary">Monto:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                            R$ {(orderData.subtotal.value / (orderData.subtotal.offset || 100)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Divider />

            <Box className={classes.footer}>
                <Box className={`${classes.statusBadge} ${orderData?.status === 'completed' ? classes.statusCompleted : classes.statusPending}`}>
                    Status: {orderData?.status === 'completed' ? 'pedido finalizado' : 'pendente'}
                </Box>
            </Box>
        </Paper>
    );
};

export default InteractiveMessageView;
